import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col items-center space-y-3 md:flex-row md:space-x-3 md:space-y-0" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", null, "Save Changes", -1)
const _hoisted_3 = { class: "mt-6 text-lg font-medium md:text-2xl text-neutral-600" }
const _hoisted_4 = { class: "block font-semibold md:inline-block text-neutral-900" }
const _hoisted_5 = { class: "w-full p-4 mx-auto my-6 border border-gray-200 rounded lg:p-6" }
const _hoisted_6 = { class: "flex items-center justify-between" }

import { onMounted, ref, computed, watch } from "vue";
import UserLayout from "@/layouts/UserLayout.vue";
import SectionTitle from "@/components/partials/SectionTitle.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";
import FormSection from "./FormSection.vue";
import { useRouter, useRoute } from "vue-router";
import { renderSelect, renderSelectV2 } from "@/interfaces/DropdownInterface";
import { PerpetratorCreate } from "@/interfaces/Cases/Perpetrator";
import PerpetratorForm from "@/classes/Forms/Cases/PerpetratorForm";
import { errorResponse } from "@/helpers/errorMessage";
import { useNetworkStore } from "@/store/network";
import pickBy from "lodash/pickBy";
import FormOptions from "@/classes/Forms/FormOptions";
import Gender from "@/enums/Gender";
import PerpetratorIdentity from "@/enums/PerpetratorIdentity";
import CurrentLocation from "@/enums/CurrentLocation";
import { useRecordStatusChecker } from "@/store/recordStatusChecker";
import Relationships from "@/classes/Forms/Relationships";
import CasePerpetratorOfflineForm from "@/classes/Forms/Cases/CasePerpetratorOfflineForm";
import { onIonViewWillEnter } from "@ionic/vue";
import OfflineCasePerpetrator from "@/classes/Forms/Cases/OfflineCasePerpetrator";
import PerpetratorService from "@/classes/Perpetrators/PerpetratorService";
import PerpetratorOfflineForm from "@/classes/Perpetrators/Offline/Formatters/PerpetratorOfflineForm";
import PerpetratorOfflineService from "@/classes/Perpetrators/Offline/OfflineService";
import Rules from "@/classes/Perpetrators/Offline/Validation/Rules";
import useVuelidate from "@vuelidate/core";
import ErrorValidation from "@/views/interventions/Components/Classess/ErrorValidation";
import OnlineSynching from "@/classes/Synching/OnlineSynching";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import PageLoader from "@/components/loaders/PageLoader.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'EditPage',
  setup(__props) {

const networkStore = useNetworkStore();
const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);
const router = useRouter();
const route = useRoute();
const showSuccess = ref<boolean>(false);
const patientId = ref<number | null>(route.params.patient);
const caseId = ref<number | null>(route.params.case);
const perpetratorId = ref<number | null>(route.params.perpetrator);
const genders = ref<renderSelectV2[]>([]);
const identities = ref<renderSelectV2[]>([]);
const nationalities = ref<renderSelect[]>([]);
const religions = ref<renderSelect[]>([]);
const relationships = ref<renderSelect[]>([]);
const currentLocations = ref<renderSelect[]>([]);
const form = ref<PerpetratorCreate>(new PerpetratorForm());
const errors = ref<object>({});
const successMessage = ref<string>("Case perpetrator has beed updated!");
const loaded = ref<boolean>(false);
const item = ref<PerpetratorCreate>(PerpetratorOfflineForm.formStructure());
const offlineParams = ref<string>(route.query.offline);
const options = new FormOptions();
const patient = ref<string>('');
const recordStatusChecker = useRecordStatusChecker();
const relationship = new Relationships()
const { createRules } = Rules;
const onlineSync = new OnlineSynching();
const showError = ref(false);
const showErrorMessage = ref(null);
const loading = ref(false);

/**
 * Validate the form using Vuelidator
 */
 const validateForm = async () =>{
	const vuelidate = useVuelidate(createRules({...form.value}), form.value)
	const result = await vuelidate.value.$validate();

    if (result) {
        return null;
    } else {
        showError.value = true;
        showErrorMessage.value = 'Please fill up all required fields';
        errors.value = ErrorValidation.getErrors(vuelidate.value);
        errorResponse(errors.value);
        return errors.value;
    }
}

const save = async (): Promise<any> => {
    loading.value = true;
	errors.value = {};
    showError.value = false;
    showErrorMessage.value = null;

    // Set validation rule for Online and Offline
    const validate = await validateForm();
    if (validate) {
        loading.value = false;
        return;
    }

    const response = await  PerpetratorService.update(
		patientId.value,
		caseId.value,
		perpetratorId.value,
		{ ...form.value },
        isNetworkAvailable.value
	);

    if (response?.status === 200 || response?.data != undefined) {
        const data = response.data.data;
        showSuccess.value = true;
        successMessage.value = response.data.message;
        if (isNetworkAvailable.value) {
            await PerpetratorOfflineService.syncToOffline(patientId.value, caseId.value, data.item);
        }
    } else {
        const error = response;
        errors.value = errorResponse(error.response?.data?.errors);
    }
    loading.value = false;
};
 
const redirectSuccess = async (): void => {
	showSuccess.value = false;
	router.replace(`/patient/${patientId.value}/cases/${caseId.value}/edit`);
};

const getData = async () => {
    loading.value = true;
    const response = await PerpetratorService.edit(
		patientId.value,
		caseId.value,
		perpetratorId.value,
		pickBy({}),
        isNetworkAvailable.value
	);

    if (response?.response?.status === 404) {
        loaded.value = false;
        return;
    }

    if (response?.status === 200 || response?.data != undefined) {
        const itemData = response.data.item;
		genders.value = response.data.genders;
		identities.value = response.data.identities;
		nationalities.value = response.data.nationalities;
		religions.value = response.data.religions;
		relationships.value = response.data.perpetratorRelationships;
		currentLocations.value = response.data.currentLocations;
        form.value = itemData ? PerpetratorOfflineForm.renderSyncToOffline(itemData) : item.value;
		patient.value = response.data.patient;
		item.value = itemData;
         
        loaded.value = true;
        loading.value = false;
    } else {
        console.log(response);
        loading.value = false;
        redirectSuccess();
    }
};

watch(
    () => isNetworkAvailable.value,
    async () => {
        loaded.value = false;
        await getData();
        await onlineSync.checkDrafts();
    }
);
 
onIonViewWillEnter(async () => {
    recordStatusChecker.isCreatedGuardian = false; 
    await getData();
    await onlineSync.checkDrafts();
});
 

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(UserLayout, {
    hasBack: "",
    backLink: `/patient/${patientId.value}/cases/${caseId.value}/edit${offlineParams.value ? '?offline=true' : ''}`,
    hasBackClass: "flex md:justify-between md:items-center md:flex-row flex-col"
  }, {
    lowerRight: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("a", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (
						_unref(router).replace(
							`/patient/${patientId.value}/cases/${caseId.value}/edit${offlineParams.value ? '?offline=true' : ''}`
						)
					)),
          class: "w-full mt-6 md:w-auto md:mt-0"
        }, [
          _createVNode(ButtonComponent, {
            btnWidth: "quinary",
            customClass: "quinary md:w-auto w-full"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Cancel ")
            ]),
            _: 1
          })
        ]),
        _createVNode(ButtonComponent, {
          customClass: "primary md:w-auto w-full md:mt-0 mt-6",
          form: "form",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (save()))
        }, {
          default: _withCtx(() => [
            _hoisted_2
          ]),
          _: 1
        })
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_3, [
        _createTextVNode("Patient's Name: "),
        _createElementVNode("span", _hoisted_4, _toDisplayString(patient.value), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(SectionTitle, { title: "Perpetrator Details" })
        ]),
        (loaded.value)
          ? (_openBlock(), _createBlock(FormSection, {
              key: 0,
              form: form.value,
              errors: errors.value,
              genders: genders.value,
              relationships: relationships.value,
              nationalities: nationalities.value,
              religions: religions.value,
              currentLocations: currentLocations.value,
              identities: identities.value,
              item: item.value,
              isEdit: false
            }, null, 8, ["form", "errors", "genders", "relationships", "nationalities", "religions", "currentLocations", "identities", "item"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(SuccessModal, {
        show: showSuccess.value,
        onConfirm: _cache[2] || (_cache[2] = ($event: any) => (redirectSuccess())),
        "action-text": "Close",
        title: "Record Save!",
        content: successMessage.value
      }, null, 8, ["show", "content"]),
      _createVNode(ErrorModal, {
        show: showError.value,
        content: showErrorMessage.value
      }, null, 8, ["show", "content"]),
      _createVNode(PageLoader, { show: loading.value }, null, 8, ["show"])
    ]),
    _: 1
  }, 8, ["backLink"]))
}
}

})